import { getAccountKey, getSchedule, getTokenInfo } from '../api/mirrorAPI';
import { isTokenAssociationParams } from '../types/transactions';
import {
  canonicalId,
  checkScheduledProposal,
  checkTokenAssociation,
  parseMirrorKey,
  ThresholdKey,
  TokenInfo,
  Verdict,
} from '../utils/proposalVerifier';

export interface ProposalForSigning {
  type: string;
  schedule_id?: string;
  signed_tx_bytes?: string;
  wallet_account_id?: string;
  network?: string;
  params: any;
}

/**
 * Where the signature will count. None of it comes from the proposal: a
 * proposal that names the signer's own account as "the wallet", or another
 * network's mirror, would otherwise choose what it is checked against.
 */
export interface SigningContext {
  /** The wallet the user has open */
  walletAccountId: string;
  /** The network the user is connected to, which is where the signature goes */
  network: string;
  /** The connected account that will sign */
  signerAccountId: string;
  /** Signing an invitation to join the wallet */
  joining?: boolean;
}

const refuse = (reason: string): Verdict => ({ ok: false, reason });

/** An account's own single key (DER), or null when it can't be read or isn't one key. */
const singleKeyOf = async (accountId: string, network: string): Promise<string | null> => {
  try {
    const key = parseMirrorKey(await getAccountKey(accountId, network));
    return key.isList ? null : key.keys[0];
  } catch {
    return null;
  }
};

/**
 * Checks, against the mirror node, that a proposal does what it shows,
 * before the user signs it. Never throws: anything unexpected is a refusal.
 */
export async function verifyProposal(proposal: ProposalForSigning, context: SigningContext): Promise<Verdict> {
  try {
    return await check(proposal, context);
  } catch {
    return refuse('The proposal could not be checked');
  }
}

async function check(proposal: ProposalForSigning, context: SigningContext): Promise<Verdict> {
  const { network, joining } = context;
  const wallet = canonicalId(context.walletAccountId);
  if (!wallet) return refuse('The wallet account is unknown');
  if (proposal.wallet_account_id && canonicalId(proposal.wallet_account_id) !== wallet) {
    return refuse(`This proposal belongs to ${proposal.wallet_account_id}, not to wallet ${wallet}`);
  }
  if (proposal.network && proposal.network !== network) {
    return refuse(`This proposal is for ${proposal.network}, but you are connected to ${network}`);
  }
  const signer = canonicalId(context.signerAccountId);
  if (wallet === signer) return refuse('The wallet cannot be your own account');

  // An ordinary account has a single key: a signature meant for a multisig
  // must never be the one that moves it
  let walletKey: ThresholdKey;
  try {
    walletKey = parseMirrorKey(await getAccountKey(wallet, network));
  } catch {
    return refuse(`Could not read the key of wallet ${wallet} from the mirror node`);
  }
  if (!walletKey.isList) return refuse(`Account ${wallet} is not a multisig wallet`);

  const params = proposal.params ?? {};

  let signerKeys: Record<string, string | null> | undefined;
  if (proposal.type === 'ThresholdUpdate') {
    const listed: any[] = Array.isArray(params.newSigners) ? params.newSigners : [];
    const ids = listed.map((s) => canonicalId(s?.accountId)).filter((id): id is string => !!id);
    signerKeys = Object.fromEntries(
      await Promise.all(ids.map(async (id) => [id, await singleKeyOf(id, network)] as const)),
    );
  }

  if (joining) {
    if (proposal.type !== 'ThresholdUpdate') return refuse('An invitation must be a wallet key update');
    if (!signer || !signerKeys || !(signer in signerKeys)) {
      return refuse('This update does not add your account to the wallet');
    }
    const ownKey = signerKeys[signer];
    if (!ownKey) return refuse('Could not read your account key from the mirror node');
    // Joining adds a key: if it's already there, the "wallet" may be an
    // account the signer controls, and the update would hand it to others
    if (walletKey.keys.includes(ownKey)) return refuse('Your key is already part of this wallet on Hedera');
  }

  let token: TokenInfo | undefined;
  if (proposal.type === 'TokenTransfer' || proposal.type === 'TokenAssociation') {
    const tokenId = canonicalId(params.tokenId);
    if (!tokenId) return refuse('The proposal names no valid token');
    try {
      token = await getTokenInfo(tokenId, network);
    } catch {
      return refuse(`Could not read token ${tokenId} from the mirror node`);
    }
  }

  if (proposal.type === 'TokenAssociation') {
    // The Sign button signs these bytes for any TokenAssociation, so the
    // proposal must also look like one wherever the UI describes it
    if (!isTokenAssociationParams(params)) return refuse('The proposal is not a plain token association');
    if (!proposal.signed_tx_bytes) return refuse('The transaction bytes are missing');
    return checkTokenAssociation(proposal.signed_tx_bytes, wallet, params, token);
  }

  // The Sign button signs this exact string, so it must be the one checked
  const scheduleId = proposal.schedule_id;
  if (!scheduleId || canonicalId(scheduleId) !== scheduleId) return refuse('The proposal has no valid schedule');
  let schedule;
  try {
    schedule = await getSchedule(scheduleId, network);
  } catch {
    return refuse(`Could not read schedule ${scheduleId} from the mirror node`);
  }
  return checkScheduledProposal(schedule, {
    type: proposal.type,
    walletAccountId: wallet,
    params,
    token,
    walletKey,
    signerKeys,
  });
}
